// ─── publishToClientApp ────────────────────────────────────────────────
// Bridge SaaS → app cliente (anissa-client-preview).
//
// Construit le ClientPlan via le mapper local (déjà validé sur vraie data),
// puis POST l'endpoint admin /api/admin/publish-plan de l'app cliente.
//
// V96.35 : passe par le proxy server-side `/api/client-app-proxy` au lieu
// d'appeler directement l'app cliente avec un Bearer token. Le secret
// VITE_CLIENT_APP_ADMIN_SECRET (qui était inliné dans le bundle JS public)
// vit maintenant côté serveur uniquement (process.env.CLIENT_APP_ADMIN_SECRET).

import { buildClientAppPlanFromConsultation } from "./clientAppMapper";
import { applyEnrichmentToPlan } from "./aiEnrichClientPlan";
import {
  saveClient,
  saveNutritionConsultationConfirmed,
} from "../store";
import { clientAppFetch, checkClientAppConfig, ClientAppConfigError, ClientAppHttpError } from "./clientAppFetch";
import { assertPlanClinicallyCleared } from "./clinicalClearance";
import { traceClinicalOverride, CLINICAL_OVERRIDE_DOORS } from "./clinicalOverrideAudit";
import { getMealPrepPublicationIssue } from "./mealPrep";
import { getFridgeSyncPublicationIssue } from "./fridgeSync";
import { getFourWeekRotationPublicationIssue } from "./fourWeekRotation";
import { getClientAppWorkspacePublicationIssue } from "./clientAppWorkspace";
import { findPublicationFoodBlockers } from "./weekMenus";
import {
  ANISSA_CLINICAL_OVERRIDE_DECISION,
  buildPlanValidationContent,
  isCoherentPlanClinicalDecisionProof,
  sha256Hex,
} from "./journeyState";
import { hasRiskProfileAssessment } from "./riskProfileFlags";
import { supabase } from "../supabaseClient";
import { mgdReviewV2Client } from "./mgdReviewV2Client";
import { externalPlanAuthorityClient } from './externalPlanAuthority';
import { isExternalPlanInput } from './externalPlanWorkflow';
import { aiMentionMessage, findAiMentionsDeepDetailed, identityNamesOf } from './aiMentionGuard';
import {
  readCurrentPlanDeliveryEvidence,
  resolveSealedFollowupWeek,
} from './planDeliveryContract';

/** Récupère l'email de la cliente depuis ses données SaaS. */
function resolveClientEmail(client) {
  return (
    client?.form?.email ||
    client?.email ||
    null
  );
}

export class PublishConfigError extends Error {
  constructor(message) {
    super(message);
    this.name = "PublishConfigError";
  }
}

export class PublishHttpError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.name = "PublishHttpError";
    this.status = status;
    this.payload = payload;
  }
}

// P1.2 (remède sécurité clinique) — la publication est l'une des 4 portes de
// sortie du plan. Le gate vit ICI (service), pas seulement dans l'UI, pour que
// tous les call sites soient couverts par une seule barrière — défense en
// profondeur. L'override passe par la décision atomique scellée à la
// validation (plans standard et externes, V97.247). Filet pour un plan externe :
// `overridable` autorise l'UI à demander une confirmation consciente, tracée
// en audit strict avant toute sortie.
export class PublishClinicalError extends Error {
  constructor(verdict, { overridable = false } = {}) {
    super("Clairance clinique refusée — plan bloqué à la publication.");
    this.name = "PublishClinicalError";
    this.verdict = verdict;
    this.overridable = overridable === true;
  }
}

const PHASE_TRANSITION_RUN_STATUSES = new Set(['ready', 'partially_proven']);

function hasPhaseTransitionRunValue(consultation) {
  return consultation?.phaseTransitionRun !== undefined
    && consultation?.phaseTransitionRun !== null;
}

function assertValidPhaseTransitionRunIfPresent(consultation) {
  if (hasPhaseTransitionRunValue(consultation)
      && !getPublishablePhaseTransition(consultation)) {
    throw new PublishConfigError(
      'La preuve de transition de phase est invalide ou incomplète. Publication refusée.',
    );
  }
}

export function getPublishablePhaseTransition(consultation) {
  const run = consultation?.phaseTransitionRun;
  if (!run || typeof run !== 'object' || Array.isArray(run)) return null;
  const consultationId = String(consultation?.id || '');
  const targetId = String(run?.targetConsultationId || '');
  const proposed = run?.proposedProtocolPhases;
  if (!consultationId || targetId !== consultationId
      || !PHASE_TRANSITION_RUN_STATUSES.has(run?.status)
      || !['app', 'app_and_document'].includes(run?.deliveryMode)
      || typeof run?.id !== 'string' || !run.id
      || typeof run?.fromPhaseId !== 'string' || !run.fromPhaseId
      || typeof run?.toPhaseId !== 'string' || !run.toPhaseId
      || (run?.deliveryMode === 'app_and_document' && !run?.documentConfirmedAt)
      || !proposed || typeof proposed !== 'object' || Array.isArray(proposed)
      || !Array.isArray(proposed.phases)) return null;
  const active = proposed.phases.filter((phase) => phase?.status === 'active');
  if (active.length !== 1 || active[0]?.id !== run.toPhaseId) return null;
  return run;
}

/** Vérifie que la config minimale est présente sans rien envoyer. */
export function checkPublishConfig() {
  return checkClientAppConfig();
}

export function resolveFollowupPublicationWeek(consultation, client = null) {
  // Une transition de phase est une version immediate du programme, pas un
  // rendez-vous S4/S8/S12/S16. Sa preuve est portee par la run dediee.
  if (getPublishablePhaseTransition(consultation)) return 0;
  const provenance = consultation?.followupData?._versionProvenance
    ?? consultation?.followup_data?._versionProvenance
    ?? null;
  if (provenance?.trigger === 'manual_program_update') {
    return provenance.workflowAction === 'manual'
      && ['targeted', 'menus_recipes', 'full'].includes(provenance.updateScope)
      ? 0
      : null;
  }
  return resolveSealedFollowupWeek(consultation, client);
}

export function buildClientAppPublicationPayload(
  client,
  consultation,
  enrichment = null,
) {
  // Fail closed: une propriété de transition présente ne doit jamais pouvoir
  // être silencieusement ignorée et retomber dans le parcours de publication
  // normal, même si la consultation cible n'est pas marquée comme suivi.
  assertValidPhaseTransitionRunIfPresent(consultation);
  const basePlan = buildClientAppPlanFromConsultation(client, consultation);
  const fourWeekRotationIssue = getFourWeekRotationPublicationIssue(
    basePlan?.sections?.week_meals,
    consultation?.editorial_overrides?.four_week_rotation,
  );
  if (fourWeekRotationIssue) throw new PublishConfigError(fourWeekRotationIssue);
  const publishesMealPrepV2 = basePlan?.sections?.week_meals?.prep?.version === 2;
  if (!publishesMealPrepV2) {
    const mealPrepIssue = getMealPrepPublicationIssue(
      basePlan?.sections?.week_meals,
      consultation?.editorial_overrides?.meal_prep,
    );
    if (mealPrepIssue) throw new PublishConfigError(mealPrepIssue);
    const fridgeSyncIssue = getFridgeSyncPublicationIssue(
      basePlan?.sections?.week_meals,
      consultation?.editorial_overrides?.meal_prep,
      basePlan?.sections?.fridge_data,
      consultation?.editorial_overrides?.fridge_sync,
    );
    if (fridgeSyncIssue) throw new PublishConfigError(fridgeSyncIssue);
  }
  const plan = enrichment ? applyEnrichmentToPlan(basePlan, enrichment) : basePlan;
  const phaseTransition = getPublishablePhaseTransition(consultation);
  return {
    email: resolveClientEmail(client),
    first_name: client?.prenom?.trim() || 'Cliente',
    mode: plan.mode ?? null,
    locale: plan.locale ?? null,
    title: plan.title ?? null,
    objective: plan.objective ?? null,
    source_consultation_id: consultation?.id,
    nutrition_plan: consultation?.nutrition_plan || consultation?.nutritionPlan || '',
    sections: plan.sections ?? null,
    journey_phases: plan.journey_phases || null,
    followup_week: resolveFollowupPublicationWeek(consultation, client),
    ...(phaseTransition
      ? {
          publication_mode: 'staged_phase_transition',
          phase_transition_id: phaseTransition.id,
        }
      : {}),
  };
}

export async function createPhaseTransitionPublicationConfirmation({
  client,
  consultation,
  enrichment = null,
  options = {},
  explicitlyConfirmed = false,
} = {}) {
  const run = getPublishablePhaseTransition(consultation);
  if (!run || explicitlyConfirmed !== true) {
    throw new PublishConfigError(
      'Anissa doit confirmer explicitement le livrable final de cette phase avant publication.',
    );
  }
  const payload = buildClientAppPublicationPayload(client, consultation, enrichment, options);

  // Le mapping et les overrides ci-dessus font partie du contrat livré. Une
  // seconde lecture, après construction du payload et juste avant son
  // approbation, ferme la course avec une modification concurrente du SaaS.
  await assertInitialPlanValidationProofServer(client, consultation);
  return Object.freeze({
    consultationId: String(consultation.id),
    transitionId: String(run.id),
    artifactSha256: await sha256Hex(JSON.stringify(payload)),
  });
}

export async function assertPhaseTransitionPublicationConfirmation({
  consultation,
  payload,
  confirmation,
} = {}) {
  const run = getPublishablePhaseTransition(consultation);
  if (!run) return null;
  const expectedSha256 = await sha256Hex(JSON.stringify(payload));
  if (!confirmation
      || confirmation.consultationId !== String(consultation.id)
      || confirmation.transitionId !== String(run.id)
      || confirmation.artifactSha256 !== expectedSha256) {
    throw new PublishConfigError(
      'Le livrable a changé depuis la confirmation d’Anissa. Relis-le puis confirme à nouveau.',
    );
  }
  return expectedSha256;
}

/** Vérifie qu'on a tout ce qu'il faut sur la cliente avant publication. */
export function checkClientReadyForPublish(client, consultation) {
  const issues = [];
  const email = resolveClientEmail(client);
  if (!email) issues.push("Cliente sans email — ajoute son email dans la fiche.");
  if (!client?.prenom?.trim()) issues.push("Cliente sans prénom.");
  // V97.13.28 — fix casing : accepte nutrition_plan (snake) OU nutritionPlan (camel)
  const planText = consultation?.nutrition_plan
    || consultation?.nutritionPlan
    || consultation?.plan_text
    || '';
  if (!planText.trim()) {
    issues.push("Consultation sans plan nutrition (rien à publier).");
  }
  if (resolveFollowupPublicationWeek(consultation, client) === null) {
    issues.push("Semaine de suivi manquante — publication différée impossible à prouver.");
  }
  return { ok: issues.length === 0, issues, email };
}

export async function assertInitialPlanValidationProof(client, consultation) {
  if (getPublishablePhaseTransition(consultation)) return true;
  const journey = client?.journey_state || client?.journeyState || {};
  const proof = journey?.plan_validation;
  const consultationId = consultation?.id ? String(consultation.id) : '';
  const planText = consultation?.nutrition_plan
    || consultation?.nutritionPlan
    || consultation?.plan_text
    || '';
  const supplementsText = consultation?.supplements || '';
  const contentSha256 = await sha256Hex(
    buildPlanValidationContent(planText, supplementsText),
  );
  const validatedAt = Date.parse(String(proof?.validated_at || ''));
  if (
    journey?.plan_validated !== true
    || !consultationId
    || String(proof?.consultation_id || '') !== consultationId
    || String(proof?.content_sha256 || '') !== contentSha256
    || !/^[a-f0-9]{64}$/.test(String(proof?.form_sha256 || ''))
    || !Number.isFinite(validatedAt)
    || !isCoherentPlanClinicalDecisionProof(proof, {
      consultationId,
      contentSha256,
    })
  ) {
    throw new PublishConfigError(
      "Cette version exacte du plan et de ses compléments doit être validée par Anissa avant publication.",
    );
  }
  return true;
}

export async function assertInitialPlanValidationProofServer(client, consultation) {
  if (getPublishablePhaseTransition(consultation)) return true;
  const clientId = client?.id ? String(client.id) : '';
  const consultationId = consultation?.id ? String(consultation.id) : '';
  if (!clientId || !consultationId) {
    throw new PublishConfigError('Preuve serveur du plan indisponible.');
  }
  let verified;
  try {
    verified = await readCurrentPlanDeliveryEvidence({ client, consultation, db: supabase });
  } catch (e) {
    throw new PublishConfigError(
      e?.message || "Le contenu de l’application a changé. Valide à nouveau avant publication.",
    );
  }
  const { data, error } = await supabase.rpc('verifier_preuve_plan_nutritionnel', {
    p_client_id: clientId,
    p_consultation_id: consultationId,
  });
  if (error || data !== true) {
    throw new PublishConfigError(
      "Le plan, ses compléments ou l’anamnèse ont changé depuis la validation. Valide à nouveau avant publication.",
    );
  }
  return verified;
}

export async function approveInitialPublicationArtifact(
  client,
  consultation,
  payload,
  phaseTransitionConfirmation = null,
) {
  const clientId = client?.id ? String(client.id) : '';
  const consultationId = consultation?.id ? String(consultation.id) : '';
  if (!clientId || !consultationId || !payload || typeof payload !== 'object') {
    throw new PublishConfigError('Livrable final impossible à approuver.');
  }
  const artifactSha256 = await sha256Hex(JSON.stringify(payload));
  await assertPhaseTransitionPublicationConfirmation({
    consultation,
    payload,
    confirmation: phaseTransitionConfirmation,
  });
  const { data, error } = await supabase.rpc('approuver_livrable_app_cliente', {
    p_client_id: clientId,
    p_consultation_id: consultationId,
    p_artifact_sha256: artifactSha256,
    p_artifact_clinical_confirmed: true,
  });
  if (error || data !== true) {
    throw new PublishConfigError(
      "Le livrable final n’a pas pu être lié à la validation d’Anissa. Rien n’a été publié.",
    );
  }
  return artifactSha256;
}

export function assertPublicationReviewScope(client, consultation) {
  assertValidPhaseTransitionRunIfPresent(consultation);
  const journeyState = client?.journey_state || client?.journeyState || {};
  if (journeyState?.delivery_proof?.mode === 'document') {
    throw new PublishConfigError(
      "Ce programme a été livré sans application. Reviens à l’étape Livraison pour changer explicitement de canal.",
    );
  }
  if (!hasRiskProfileAssessment(client?.form)) {
    throw new PublishConfigError(
      "Anissa doit confirmer l’évaluation des profils à risque avant toute publication.",
    );
  }
  const phaseTransition = getPublishablePhaseTransition(consultation);
  if (phaseTransition && String(phaseTransition.clientId || '') !== String(client?.id || '')) {
    throw new PublishConfigError('La transition de phase ne correspond pas à cette cliente.');
  }
  return true;
}

export function collectPublicationText(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(collectPublicationText).filter(Boolean).join('\n');
  if (typeof value === 'object') {
    return Object.values(value).map(collectPublicationText).filter(Boolean).join('\n');
  }
  return '';
}

/**
 * Contrôle des exclusions alimentaires sur le payload final d'un client MGD.
 * Lève une PublishConfigError (non contournable) au premier lot de blocages.
 */
export function assertMgdAppFoodExclusions({ client, consultation, payload }) {
  const blockers = findPublicationFoodBlockers({ client, consultation, payload });
  if (blockers.length > 0) {
    const error = new PublishConfigError(blockers[0]);
    error.issues = blockers;
    throw error;
  }
  return true;
}

/**
 * Aucune mention d'outil ou d'IA dans ce que la cliente verra (titre,
 * objectif, plan, sections, parcours), pour toutes les clientes, MGD ou non.
 * Non contournable.
 */
export function assertNoAiMentionInPublication(payload, client = null) {
  const { certain: mentions, possible } = findAiMentionsDeepDetailed({
    title: payload?.title,
    objective: payload?.objective,
    nutrition_plan: payload?.nutrition_plan,
    sections: payload?.sections,
    journey_phases: payload?.journey_phases,
  }, { identityNames: identityNamesOf(client) });
  // Seules les mentions sans ambiguïté bloquent un contenu déjà existant ;
  // les autres (« IA », préambule…) ont déjà été bloquées à la création des
  // lots et sont rendues à l'appelant pour information.
  if (mentions.length > 0) {
    const error = new PublishConfigError(`Publication bloquée — ${aiMentionMessage(mentions)}`);
    error.issues = [error.message];
    throw error;
  }
  return { possible };
}

export function assertPublicationClinicalClearance(planText, form, { overridable = false } = {}) {
  const clearance = assertPlanClinicallyCleared(planText, { form });
  if (!clearance.cleared) throw new PublishClinicalError(clearance, { overridable });
  return true;
}

export async function activatePhaseTransitionClientAppPlan({
  client,
  run,
  stagedPublishResult,
  fetchClientApp = clientAppFetch,
} = {}) {
  const artifactSha256 = String(stagedPublishResult?.publication_artifact_sha256 || '');
  const planId = String(stagedPublishResult?.plan_id || '');
  const sourceConsultationId = String(stagedPublishResult?.source_consultation_id || '');
  if (run?.status !== 'finalized'
      || !['app', 'app_and_document'].includes(run?.deliveryMode)
      || String(run?.clientId || '') !== String(client?.id || '')
      || sourceConsultationId !== String(run?.targetConsultationId || '')
      || planId !== String(run?.appPlanId || '')
      || stagedPublishResult?.staged !== true
      || stagedPublishResult?.status !== 'draft'
      || !planId
      || !/^[0-9a-f]{64}$/.test(artifactSha256)
      || artifactSha256 !== String(run?.appArtifactSha256 || '')) {
    throw new PublishConfigError(
      'Le brouillon app ne correspond pas à la transition finalisée. Activation refusée.',
    );
  }
  const payload = {
    plan_id: planId,
    source_consultation_id: sourceConsultationId,
    publication_artifact_sha256: artifactSha256,
  };
  // Une activation peut avoir lieu bien après le staging. Relire au dernier
  // instant et lier une nouvelle autorisation au même artifact; si aucun MGD
  // n'est encore actif, le proxy reste l'ultime garde nullable contre 0→1.
  const mgdStatus = await mgdReviewV2Client.status(String(client.id));
  let mgdAuthorization = null;
  if (mgdStatus.reports.length > 0) {
    const proof = await mgdReviewV2Client.planProofStatus({ clientId: String(client.id),
      consultationId: sourceConsultationId });
    if (!proof.authorized || !proof.persisted || !proof.planHash) {
      throw new PublishConfigError(
        'La preuve MGD de cette transition n’est plus autorisée. Activation bloquée.',
      );
    }
    mgdAuthorization = await mgdReviewV2Client.authorizeOutput({ clientId: String(client.id),
      consultationId: sourceConsultationId, channel: 'client_app_publication',
      planHash: proof.planHash, artifactSha256 });
  }
  let body;
  try {
    body = await fetchClientApp('/api/admin/activate-plan', {
      method: 'POST',
      payload,
      proof: {
        transition_id: String(run.id),
        plan_id: planId,
        client_id: String(client.id),
        consultation_id: sourceConsultationId,
        artifact_sha256: artifactSha256,
        ...(mgdAuthorization
          ? { mgd_authorization_id: mgdAuthorization.authorizationId }
          : {}),
      },
    });
  } catch (error) {
    if (error instanceof ClientAppConfigError) throw new PublishConfigError(error.message);
    if (error instanceof ClientAppHttpError) {
      throw new PublishHttpError(error.message, error.status, error.payload);
    }
    throw error;
  }
  if (body?.ok !== true
      || body?.status !== 'published'
      || body?.staged === true
      || String(body?.plan_id || '') !== planId
      || String(body?.source_consultation_id || '') !== sourceConsultationId
      || String(body?.publication_artifact_sha256 || '') !== artifactSha256) {
    throw new PublishHttpError(
      body?.error || body?.message || 'Activation app non confirmée',
      0,
      body,
    );
  }
  return {
    ...body,
    staged: false,
    publication_artifact_sha256: artifactSha256,
    source_consultation_id: sourceConsultationId,
    phase_transition_id: run.id,
  };
}

/**
 * Publie une consultation vers l'app cliente.
 *
 * @param {object} client       - ligne `clients` du SaaS
 * @param {object} consultation - ligne `nutrition_consultations` (état courant)
 * @param {object|null} [enrichment] - enrichissement IA accepté par Anissa (optionnel).
 *                                     Si fourni, mergé sur le plan avant publication.
 * @param {object} [options] - options de confirmation de transition.
 * @returns {Promise<object>}   - { ok, plan_id, status, published_version, effective_at, login_url, ... }
 */
export async function publishConsultationToClientApp(client, consultation, enrichment = null, options = {}) {
  // 1. Vérif config
  const cfg = checkPublishConfig();
  if (!cfg.ok) {
    throw new PublishConfigError(cfg.issues.join(" • "));
  }

  // V97.247 — la modale peut tenir une fiche chargée avant la validation (cas
  // d'une V2 de suivi validée juste avant l'ouverture de l'aperçu). La preuve
  // de validation est donc relue en base, jamais prise dans l'état React.
  if (client?.id) {
    const { data: freshClient, error: freshError } = await supabase
      .from('clients')
      .select('journey_state')
      .eq('id', client.id)
      .maybeSingle();
    if (freshError || !freshClient) {
      throw new PublishConfigError('Fiche cliente illisible : recharge la page puis réessaie.');
    }
    client = { ...client, journey_state: freshClient.journey_state };
  }

  // 2. Vérif données cliente
  const ready = checkClientReadyForPublish(client, consultation);
  if (!ready.ok) {
    throw new PublishConfigError(ready.issues.join(" • "));
  }

  // Le bouton de la modale n'est pas une autorité suffisante : tout appel au
  // service de publication doit retrouver le dossier application appliqué,
  // relu dans ses sept sections et encore lié aux sources courantes.
  const workspaceIssue = getClientAppWorkspacePublicationIssue({
    client,
    consultation,
    directives: consultation?.aiDirectives || consultation?.ai_directives || '',
  });
  if (workspaceIssue) {
    throw new PublishConfigError(workspaceIssue);
  }

  // Le statut affiché dans la modale n'est jamais une autorisation. Relire
  // l'autorité MGD au clic final, avant toute préparation d'egress.
  if (enrichment != null) {
    throw new PublishConfigError(
      'Enregistre d’abord les améliorations dans le SaaS, puis valide à nouveau le plan avant publication.',
    );
  }
  const mgdStatus = await mgdReviewV2Client.status(String(client?.id || ''));
  const planTextForClearance = consultation?.nutrition_plan
    || consultation?.nutritionPlan
    || consultation?.plan_text
    || '';
  const planHash = await sha256Hex(String(planTextForClearance).replace(/\r\n/g, '\n'));
  const externalInput = consultation?.editorial_overrides?.external_plan_input
    ?? consultation?.editorialOverrides?.external_plan_input;
  const usesExternalMode = isExternalPlanInput(externalInput);
  const hasActiveMgd = mgdStatus.reports.length > 0 && !usesExternalMode;
  if (usesExternalMode) {
    const externalAuthority = await externalPlanAuthorityClient.status({
      clientId: String(client.id), consultationId: String(consultation.id), planHash,
      profileSummaryHash: externalInput.profile_summary_sha256,
    });
    if (!externalAuthority.authorized || !externalAuthority.persisted
        || !externalAuthority.validated) {
      throw new PublishConfigError(
        'Le plan externe courant doit être enregistré et validé avant publication.',
      );
    }
  }
  if (hasActiveMgd) {
    const proof = await mgdReviewV2Client.planProofStatus({ clientId: String(client.id),
      consultationId: String(consultation.id || '') });
    if (!proof.authorized || !proof.persisted || proof.planHash !== planHash) {
      throw new PublishConfigError(
        'Le texte courant ne correspond plus au plan autorisé et enregistré. Publication bloquée.',
      );
    }
  }

  assertPublicationReviewScope(client, consultation);

  // La décision atomique est relue avant toute exception au gate local. Une
  // transition de phase suit son contrat distinct et ne peut jamais injecter
  // un override initial. Le helper refuse aussi tout override MGD/externe.
  await assertInitialPlanValidationProof(client, consultation);
  const phaseTransition = getPublishablePhaseTransition(consultation);
  const hasAtomicClinicalOverride = !phaseTransition
    && client?.journey_state?.plan_validation?.decision
      === ANISSA_CLINICAL_OVERRIDE_DECISION;
  // Plan externe : depuis V97.247 l'override est normalement scellé à la
  // validation, comme pour un plan standard. Filet de secours : une alerte qui
  // n'apparaît qu'à la publication (contenu de l'app) peut être confirmée
  // explicitement par Anissa, tracée en audit strict. Jamais pour MGD, une
  // transition de phase ou un plan standard.
  const externalOverrideAllowed = usesExternalMode && !phaseTransition;
  if (options?.clinicalOverride === true && !externalOverrideAllowed) {
    throw new PublishConfigError(
      'Override clinique de publication réservé aux plans externes. Valide à nouveau le plan.',
    );
  }
  // Alertes déjà confirmées par Anissa dans la décision scellée : seules
  // celles-ci peuvent être tolérées sur le contenu final de l'app.
  const sealedOverrideLabels = new Set(hasAtomicClinicalOverride
    ? (client?.journey_state?.plan_validation?.clinical_override?.snapshot?.verdict?.violations || [])
      .map((item) => item?.label).filter(Boolean)
    : []);
  const enforcePublicationClearance = async (text, { toleratesSealed = false } = {}) => {
    try {
      assertPublicationClinicalClearance(text, client?.form, {
        overridable: externalOverrideAllowed,
      });
    } catch (caught) {
      let error = caught;
      if (error instanceof PublishClinicalError && toleratesSealed) {
        const unsealed = (error.verdict?.violations || [])
          .filter((item) => !sealedOverrideLabels.has(item?.label));
        if (unsealed.length === 0) return;
        error = new PublishClinicalError(
          { ...error.verdict, violations: unsealed },
          { overridable: externalOverrideAllowed },
        );
      }
      if (!(error instanceof PublishClinicalError) || !externalOverrideAllowed
          || options?.clinicalOverride !== true) {
        throw error;
      }
      await traceClinicalOverride(error.verdict, CLINICAL_OVERRIDE_DOORS.PUBLISH_APP, {
        clientId: String(client.id),
        consultationId: String(consultation.id),
        strict: true,
      });
    }
  };

  // 2bis. P1.2 — Clairance clinique (fail-closed). Bloque la publication d'un
  // plan présentant une violation HIGH (allergène, phrase interdite, interaction
  // contre-indiquée) sauf override conscient explicite de l'UI.
  if (!hasActiveMgd && !hasAtomicClinicalOverride) {
    await enforcePublicationClearance(planTextForClearance);
  }

  // La publication initiale doit correspondre octet pour octet à la version
  // atomiquement validée. Une édition du plan ou des compléments invalide la
  // preuve et impose une nouvelle validation explicite d'Anissa.
  const initialDeliveryProof = phaseTransition
    ? null
    : await assertInitialPlanValidationProofServer(client, consultation);

  // 3. Construit le plan via le mapper local + applique l'enrichissement IA
  // si fourni (Anissa l'a explicitement validé via le bouton "Utiliser").
  const preparedPayload = buildClientAppPublicationPayload(
    client,
    consultation,
    enrichment,
    options,
  );
  const finalDeliveryProof = phaseTransition
    ? null
    : await assertInitialPlanValidationProofServer(client, consultation);
  if (finalDeliveryProof
      && (finalDeliveryProof.evidence.evidenceId !== initialDeliveryProof.evidence.evidenceId
      || finalDeliveryProof.evidence.appArtifactSha256
        !== initialDeliveryProof.evidence.appArtifactSha256)) {
    throw new PublishConfigError(
      'Le contenu de l’application a changé pendant sa préparation. Valide à nouveau le plan.',
    );
  }

  // Pour une livraison initiale, la preuve courante contient déjà le payload
  // exact scellé lors de la validation. Le reconstruire ici créait une seconde
  // représentation susceptible de diverger sur des champs optionnels ou des
  // normalisations, alors que le contenu métier était identique.
  const payload = phaseTransition
    ? preparedPayload
    : finalDeliveryProof?.contract?.sealedAppPayload;
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new PublishConfigError('Le livrable application scellé est indisponible.');
  }

  // Les éditions de l'aperçu et l'enrichissement accepté peuvent modifier le
  // contenu après la validation du markdown source. Le livrable structuré final
  // repasse donc intégralement par la clairance avant son approbation serveur.
  // Même après une confirmation scellée, le contenu final de l'app (rotation,
  // meal prep, frigo…) n'a jamais été montré à Anissa : il est toujours
  // contrôlé, et seules les alertes qu'elle a déjà confirmées sont tolérées.
  if (!hasActiveMgd) {
    // Texte réel, une valeur par ligne : sur du JSON sérialisé, un retour à la
    // ligne devient « \n » et masquait l'allergène placé en début de ligne.
    await enforcePublicationClearance(collectPublicationText({
      title: payload.title,
      objective: payload.objective,
      nutrition_plan: payload.nutrition_plan,
      sections: payload.sections,
      journey_phases: payload.journey_phases,
    }), { toleratesSealed: hasAtomicClinicalOverride });
  }

  // Client MGD : les deux clairances locales ci-dessus ne s'appliquent pas.
  // La preuve MGD ne porte QUE sur le texte du plan : tout le reste du
  // payload de l'app (menus, recettes, rotation, Meal Prep, frigo, titre,
  // objectif, introduction, stratégie, compléments, parcours) est donc
  // contrôlé ici contre les exclusions explicites de l'anamnèse. Fail-closed,
  // sans override MGD : seuls les conflits de menus confirmés par Anissa pour
  // le texte exact sont acceptés.
  if (hasActiveMgd) {
    assertMgdAppFoodExclusions({ client, consultation, payload });
  }
  // Toutes les clientes : jamais de mention d'outil ou d'IA dans l'app.
  assertNoAiMentionInPublication(payload, client);

  // Le hash porte sur le payload FINAL, après mapping et enrichissement
  // explicitement accepté. L'approbation précède immédiatement l'egress.
  const publicationArtifactSha256 = await approveInitialPublicationArtifact(
    client,
    consultation,
    payload,
    options?.phaseTransitionConfirmation || null,
  );
  const mgdAuthorization = hasActiveMgd
    ? await mgdReviewV2Client.authorizeOutput({ clientId: String(client.id),
      consultationId: String(consultation.id), channel: 'client_app_publication',
      planHash, artifactSha256: publicationArtifactSha256 })
    : null;

  let body;
  try {
    body = await clientAppFetch("/api/admin/publish-plan", {
      method: "POST",
      payload,
      proof: {
        client_id: String(client.id),
        consultation_id: String(consultation.id),
        artifact_sha256: publicationArtifactSha256,
        ...(finalDeliveryProof ? {
          delivery_evidence_id: finalDeliveryProof.evidence.evidenceId,
          delivery_app_artifact_sha256: finalDeliveryProof.evidence.appArtifactSha256,
        } : {}),
        ...(mgdAuthorization
          ? { mgd_authorization_id: mgdAuthorization.authorizationId }
          : {}),
      },
    });
  } catch (err) {
    if (err instanceof ClientAppConfigError) throw new PublishConfigError(err.message);
    if (err instanceof ClientAppHttpError) throw new PublishHttpError(err.message, err.status, err.payload);
    throw err;
  }
  if (!body?.ok) {
    throw new PublishHttpError(body?.error || body?.message || "Reponse invalide", 0, body);
  }
  if (phaseTransition && (
    body?.staged !== true
    || body?.status !== 'draft'
    || !body?.plan_id
    || String(body?.source_consultation_id || '') !== String(consultation.id)
    || String(body?.publication_artifact_sha256 || '') !== publicationArtifactSha256
  )) {
    throw new PublishHttpError('Brouillon de transition app invalide', 0, body);
  }

  // V94.52 : trace SaaS-side de la publication. Permet a ClientAppPanel
  // de presumer que la cliente a acces a l'app meme si /api/admin/clients-status
  // retourne found:false (cache stale, email mismatch, etc.).
  try {
    if (!body?.staged && client?.id) {
      const KEY = "bfc_published_client_ids";
      const raw = localStorage.getItem(KEY);
      const arr = raw ? JSON.parse(raw) : [];
      const set = new Set(Array.isArray(arr) ? arr : []);
      set.add(String(client.id));
      localStorage.setItem(KEY, JSON.stringify([...set]));
    }
  } catch {
    /* silent : pas grave si quota plein */
  }

  // V94.66 : persiste l'id staging (= clients.id cote app cliente) sur le
  // client SaaS local. Permet ensuite a fetchClientsStatus de matcher la
  // cliente directement par client_id, sans dependre de l'email — crucial
  // pour les comptes App Store / Play Store ou Apple/Google peuvent
  // remplacer l'email par hide-my-email.
  try {
    const stagingId = body?.client_id ? String(body.client_id) : null;
    if (!body?.staged && stagingId && client?.id && client.stagingClientId !== stagingId) {
      saveClient({ id: client.id, stagingClientId: stagingId });
    }
  } catch {
    /* silent : la publication a reussi, le mapping local est best-effort */
  }

  // V97.80 : tampon de publication PAR VERSION sur la consultation source.
  // Permet a la frise de Suivi de taguer chaque version « publiee » (option 1
  // SaaS-only, sans repo app cliente). Le tampon est applique exclusivement a
  // la revision capturee avant l'egress. Une revision concurrente doit produire
  // un conflit CAS, jamais recevoir le tampon d'un artefact different.
  let saasPublicationStampWarning = null;
  try {
    if (!body?.staged && consultation?.id && client?.id) {
      const nowISO = new Date().toISOString();
      const stamp = {
        published_at: body?.published_at || nowISO,
        effective_at: body?.effective_at || null,
        published_version: body?.published_version ?? null,
        plan_status: body?.status || null,
        stamped_at: nowISO,
      };
      await saveNutritionConsultationConfirmed({
        ...consultation,
        clientId: client.id,
        client_app_publication: stamp,
      });
    }
  } catch (stampError) {
    // L'egress app est déjà réussi : ne pas l'annoncer en échec. En revanche,
    // le tampon SaaS n'est plus présenté comme acquis silencieusement et peut
    // être repris séparément depuis la réponse canonique.
    saasPublicationStampWarning = stampError?.message
      || 'Publication réussie, mais tampon SaaS non confirmé.';
  }

  return {
    ...body,
    publication_artifact_sha256: publicationArtifactSha256,
    source_consultation_id: String(consultation.id),
    phase_transition_id: getPublishablePhaseTransition(consultation)?.id || null,
    saas_publication_stamp_warning: saasPublicationStampWarning,
  };
}

/**
 * V94.52 : Verifie si une cliente a ete publiee au moins une fois cote SaaS.
 * Utilise par ClientAppPanel.OverviewTab pour determiner l'acces.
 */
export function hasBeenPublishedLocally(clientId) {
  if (!clientId) return false;
  try {
    const raw = localStorage.getItem("bfc_published_client_ids");
    if (!raw) return false;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) && arr.includes(String(clientId));
  } catch {
    return false;
  }
}

/**
 * V94.53 : Backfill automatique du flag local pour une cliente.
 * Appele quand on detecte qu'elle a ete publiee via un autre signal
 * (api found OU app_enabled). Evite de devoir re-publier manuellement
 * toutes les clientes existantes apres l'update V94.52.
 */
export function markPublishedLocally(clientId) {
  if (!clientId) return;
  try {
    const KEY = "bfc_published_client_ids";
    const raw = localStorage.getItem(KEY);
    const arr = raw ? JSON.parse(raw) : [];
    const set = new Set(Array.isArray(arr) ? arr : []);
    if (!set.has(String(clientId))) {
      set.add(String(clientId));
      localStorage.setItem(KEY, JSON.stringify([...set]));
    }
  } catch {
    /* silent */
  }
}
